/**
 * MSSQL built-in MATH function signatures — one family slice of the static
 * catalog composed by `mssqlBuiltinRoutines.ts` (see that file's header for
 * why this exists and the precedence rule vs. user routines).
 *
 * `PI()` is excluded (zero-arg, no useful hint) — see
 * `MSSQL_EXCLUDED_FROM_SIGNATURE_HELP` in the barrel.
 */

import type { RoutineMetadata, RoutineParameter } from '../../../../types/electron';

type Entry = RoutineMetadata & { documentation?: string };

function p(name: string, dataType: string): RoutineParameter {
  return { name, dataType };
}

function opt(name: string, dataType: string): RoutineParameter {
  return { name: `[${name}]`, dataType };
}

function entry(name: string, parameters: RoutineParameter[], documentation?: string): Entry {
  return { schema: '', name, parameters, ...(documentation ? { documentation } : {}) };
}

export const MSSQL_MATH_ROUTINES: ReadonlyMap<string, Entry> = new Map([
  ['abs', entry('ABS', [p('numeric_expression', 'numeric')], 'Return the absolute (positive) value.')],
  ['acos', entry('ACOS', [p('float_expression', 'float')], 'Return the angle in radians whose cosine is the given float.')],
  ['asin', entry('ASIN', [p('float_expression', 'float')], 'Return the angle in radians whose sine is the given float.')],
  ['atan', entry('ATAN', [p('float_expression', 'float')], 'Return the angle in radians whose tangent is the given float.')],
  ['atn2', entry('ATN2', [p('float_expression1', 'float'), p('float_expression2', 'float')], 'Return the angle in radians between the positive x-axis and the ray to (x, y).')],
  ['ceiling', entry('CEILING', [p('numeric_expression', 'numeric')], 'Return the smallest integer greater than or equal to the value.')],
  ['cos', entry('COS', [p('float_expression', 'float')], 'Return the trigonometric cosine of the given angle in radians.')],
  ['cot', entry('COT', [p('float_expression', 'float')], 'Return the trigonometric cotangent of the given angle in radians.')],
  ['degrees', entry('DEGREES', [p('numeric_expression', 'numeric')], 'Convert an angle in radians to degrees.')],
  ['exp', entry('EXP', [p('float_expression', 'float')], 'Return the exponential value of the given float.')],
  ['floor', entry('FLOOR', [p('numeric_expression', 'numeric')], 'Return the largest integer less than or equal to the value.')],
  ['log', entry('LOG', [p('float_expression', 'float'), opt('base', 'int')], 'Return the natural logarithm, or logarithm to the given base (2012+).')],
  ['log10', entry('LOG10', [p('float_expression', 'float')], 'Return the base-10 logarithm of the given float.')],
  ['power', entry('POWER', [p('float_expression', 'float'), p('y', 'float')], 'Raise a value to the power of another value.')],
  ['radians', entry('RADIANS', [p('numeric_expression', 'numeric')], 'Convert an angle in degrees to radians.')],
  ['rand', entry('RAND', [opt('seed', 'int')], 'Return a pseudo-random float between 0 and 1, optionally seeded.')],
  ['round', entry('ROUND', [p('numeric_expression', 'numeric'), p('length', 'int'), opt('function', 'int')], 'Round a value to a given length; nonzero function truncates instead.')],
  ['sign', entry('SIGN', [p('numeric_expression', 'numeric')], 'Return the sign of the value: +1, 0, or -1.')],
  ['sin', entry('SIN', [p('float_expression', 'float')], 'Return the trigonometric sine of the given angle in radians.')],
  ['sqrt', entry('SQRT', [p('float_expression', 'float')], 'Return the square root of the given float.')],
  ['square', entry('SQUARE', [p('float_expression', 'float')], 'Return the square of the given float.')],
  ['tan', entry('TAN', [p('float_expression', 'float')], 'Return the trigonometric tangent of the given angle in radians.')],
]);
